import { CALENDLY_URL, CONTACT } from '../data/roadmapContent'
import { formatCurrency, reviewDate } from './roadmapCalcs'
import type { Pillar } from './savingsPillars'
import type { PayFrequency } from '../types/roadmap'

/** YYYYMMDD, as required for an all-day VEVENT. */
function icsDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

function stamp(date: Date): string {
  return `${icsDate(date)}T090000Z`
}

/** Escape the characters that carry meaning inside an ICS text value. */
function esc(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
}

function dayAfter(date: Date): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + 1)
  return next
}

interface Event {
  uid: string
  start: Date
  summary: string
  description: string
}

function toVevent(event: Event, created: Date): string {
  return [
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `DTSTAMP:${stamp(created)}`,
    // All-day events: DTEND is exclusive, so it lands on the following day.
    `DTSTART;VALUE=DATE:${icsDate(event.start)}`,
    `DTEND;VALUE=DATE:${icsDate(dayAfter(event.start))}`,
    `SUMMARY:${esc(event.summary)}`,
    `DESCRIPTION:${esc(event.description)}`,
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    `DESCRIPTION:${esc(event.summary)}`,
    'END:VALARM',
    'END:VEVENT',
  ].join('\r\n')
}

/**
 * Build the check-in calendar as an ICS file.
 *
 * A website cannot write into someone's calendar without their account and a
 * server, and this tool has neither by design. What it can do is hand them a
 * standard file their calendar app opens and adds in one confirmation — built
 * entirely on their own device, with nothing transmitted.
 */
export function buildCheckInCalendar(
  pillars: Pillar[],
  perPay: number,
  payFrequency: PayFrequency | null,
  createdOn: Date = new Date(),
): string | null {
  const dated = pillars.filter((pillar) => pillar.date !== null)
  if (dated.length === 0) return null

  const rate =
    perPay > 0 && payFrequency
      ? `${formatCurrency(perPay)} ${payFrequency}`
      : 'your planned amount'

  const footer = [
    `This plan assumes you are saving ${rate}.`,
    'All figures are estimates only, based on the roadmap you built on ' +
      `${createdOn.toLocaleDateString('en-AU')}. They do not update on their own — ` +
      'if your income, expenses or savings change, redo your roadmap.',
    '',
    `Book a call with Liam: ${CALENDLY_URL}`,
    `${CONTACT.name} · ${CONTACT.email}`,
  ].join('\n')

  const events: Event[] = dated.map((pillar) => ({
    uid: `sfs-checkin-${pillar.index}-${icsDate(createdOn)}@scalefinancestrategies.com.au`,
    start: pillar.date as Date,
    summary: `Savings check-in ${pillar.index} of 3 — ${formatCurrency(pillar.cumulative)}`,
    description: [
      `Milestone ${pillar.index} of 3.`,
      `By today you should have saved ${formatCurrency(pillar.cumulative)} toward your property goal.`,
      `This stage on its own: ${formatCurrency(pillar.amount)}.`,
      '',
      'Check your balance against that number. Behind? Look at what changed. Ahead? Keep going.',
      '',
      footer,
    ].join('\n'),
  }))

  const review = reviewDate(createdOn)
  events.push({
    uid: `sfs-review-90-${icsDate(createdOn)}@scalefinancestrategies.com.au`,
    start: review,
    summary: 'Review my property savings roadmap (90 days)',
    description: [
      'Ninety days since you built your roadmap — time for a look.',
      '',
      'Is the automatic transfer still running? Has your income, rent or any debt changed? Has your target property price moved?',
      'If any of those shifted, your numbers have too.',
      '',
      footer,
    ].join('\n'),
  })

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Scale Finance Strategies//Savings Roadmap//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...events.map((event) => toVevent(event, createdOn)),
    'END:VCALENDAR',
    '',
  ].join('\r\n')
}

export function downloadCalendar(ics: string, fileName: string): void {
  const blob = new Blob([ics], {
    type: 'text/calendar;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
